import React, { useMemo } from "react";
import type { PageRegistry } from "./pageTypes";
import type {
  LegacyPageConfig,
  LegacyPageContext,
  LegacyPageMeta,
  LegacyMenuMeta,
  LegacyMenuPlacement,
} from "./legacyPageRegistryTypes";
import type { IFeatures } from "@/utils/interfaces";

import { currentFeatures } from "@/configs/Settings";
import { useParams } from "react-router-dom";

import { useSessionContext } from "@/contexts/SessionContext";
import { useViewContext } from "@/contexts/ViewContext";
import { useHealthPlanContractContext } from "@/contexts/HealthPlanContractContext";

// Hooks de páginas do legado
import {
  useAccessHistory,
  useConsent,
  useEHRRecords,
  useHome,
  useManagePractitioners,
  usePrivacy,
  useProfessionalRegister,
  useProfile,
  useProfileClinic,
  useScheduleManager,
  useScheduler,
  useScheduleViewer,
  useSchedulingSearch,
  useTerm,
  useExamSolicitation,
  useMedicalPrescription,
  useMedicalNote,
  usePersonPdf,
  useQuestionnaireRespondPage,
} from "@/pages";

import { useMyRecords } from "@/pages/ehrrecords/useMyRecords";
import { usePatientSupportingDocumentsPage } from "@/pages/patientdocuments/usePatientSupportingDocumentsPage";
import { useMyfamily } from "@/pages/myfamily/useMyfamily";
import { useWearableObservations } from "@/pages/wearableobservations/useWearableObservations";
import { useManageProtocol } from "@/pages/manageprotocol/useManageProtocol";
import { useManageQuestionnaire } from "@/pages/managequestionnaire/useManageQuestionnaire";
import { useCreateProtocol } from "@/pages/createprotocol/useCreateProtocol";
import { useCreateQuestionnarie } from "@/pages/createquestionnarie/useCreateQuestionnarie";
import { useFluxExtensions } from "@/pages/fluxextensions/useFluxExtensions";
import { useAdminInsightsPage } from "@/pages/adminInsights/useAdminInsightsPage";
import { useMedicalCare } from "@/pages/medicalcare/useMedicalCare";
import { usePatientInsurance } from "@/pages/patientinsurance/usePatientInsurance";
import { useManagePlans } from "@/pages/manageplans/useManagePlans";
import { useInsuranceGuide } from "@/pages/insuranceguide/useInsuranceGuide";
import { useClinicalAnalysis } from "@/pages/clinicalanalisis/useClinicalAnalysis";
import { useFluxzapCampaigns } from "@/pages/costumerrelationship/useFluxzapCampaigns";
import { useProntoAtendimento } from "@/pages/prontoatendimento/useProntoAtendimento";
import { useRegister } from "@/pages/register/useRegister";
import { useScheduleIntegrator } from "@/pages/scheduleiframegenetator/useScheduleIntegrator";

import { InsightsSettings } from "@/pages/insightssettings/InsightsSettings";
import { ProtocolSettings } from "@/pages/protocolsettings/ProtocolSettings";
import { InsuranceSettings } from "@/pages/insurancesettings/InsuranceSettings";
import { ScheduleSettings as ScheduleSettingsHub } from "@/pages/scheduleSettings/scheduleSettings";
import { Settings as SettingsHub } from "@/pages/settings/Settings";

import {
  HealthAndSafety,
  Insights,
  NotesRounded,
  Settings as SettingsIcon,
  Today,
} from "@mui/icons-material";

/**
 * Extrai o nome da página (sem barra) a partir do `path` legado.
 * Ex.: "/home" → "home"
 */
function getPageNameFromPath(path: string): string {
  return path.startsWith("/") ? path.slice(1) : path;
}

function buildLegacyPageMetaFromFeature(feature: IFeatures): LegacyPageMeta {
  const pageName = getPageNameFromPath(feature.path);

  const menuPlacement: LegacyMenuPlacement | null = feature.sideMenu
    ? "sidebar"
    : feature.avatarMenu
      ? "avatar"
      : null;

  const menu: LegacyMenuMeta | undefined =
    menuPlacement !== null
      ? {
          placement: menuPlacement,
          id: `legacy-${pageName}`,
          label: feature.text,
          icon: feature.icon ? React.createElement(feature.icon) : undefined,
          order:
            menuPlacement === "sidebar"
              ? feature.sideMenuIndex ?? 0
              : feature.avatarIndex ?? 0,
          parentLabel: feature.parent,
        }
      : undefined;

  return {
    menu,
    isEnabled: () => Boolean(feature.active),
    legacyFeature: feature,
  };
}

/**
 * Contexto do legado usado por guards e visibilidade de menu.
 */
export function useLegacyPageContext(): LegacyPageContext {
  const session = useSessionContext();
  const view = useViewContext();
  const { contractHealthPlan } = useHealthPlanContractContext();

  return useMemo(
    () => ({
      session,
      view,
      healthPlanContract: contractHealthPlan,
    }),
    [contractHealthPlan, session, view],
  );
}

/**
 * Constrói o pageRegistry do legado.
 *
 * Nesta etapa, o registry é construído a partir dos hooks de páginas existentes.
 * Isso reduz risco e permite migração incremental para um registry declarativo.
 */
export function useLegacyPageRegistry(): PageRegistry<LegacyPageConfig> {
  const { access } = useSessionContext();
  const { availableRoles } = useViewContext();
  const { contractHealthPlan } = useHealthPlanContractContext();

  // Páginas reais (hooks do legado)
  const home = useHome();
  const term = useTerm();
  const medicalcare = useMedicalCare();
  const accesshistory = useAccessHistory();
  const consents = useConsent();
  const managePractitioners = useManagePractitioners();
  const professinalRegister = useProfessionalRegister();
  const register = useRegister();
  const profile = useProfile();
  const profileClinic = useProfileClinic();
  const scheduler = useScheduler();
  const scheduleManager = useScheduleManager();
  const schedulingSearch = useSchedulingSearch();
  const privacy = usePrivacy();
  const ehrrecords = useEHRRecords();
  const myRecords = useMyRecords();
  const patientSupportingDocuments = usePatientSupportingDocumentsPage();
  const examSolicitation = useExamSolicitation();
  const medicalPrescription = useMedicalPrescription();
  const medicalNote = useMedicalNote();
  const myfamily = useMyfamily();
  const wearableObservations = useWearableObservations();
  const manageprotocol = useManageProtocol();
  const managequestionnaire = useManageQuestionnaire();
  const createprotocol = useCreateProtocol();
  const createquestionnarie = useCreateQuestionnarie();
  const personPdf = usePersonPdf();
  const questionnaireRespondPage = useQuestionnaireRespondPage();
  const fluxExtensions = useFluxExtensions();
  const statsPanel = useAdminInsightsPage();
  const patientInsurance = usePatientInsurance();
  const managePlans = useManagePlans();
  const insuranceGuide = useInsuranceGuide();
  const clinicalAnalysis = useClinicalAnalysis();
  const fluxzapCampaigns = useFluxzapCampaigns();
  const prontoAtendimento = useProntoAtendimento();
  const scheduleIntegrator = useScheduleIntegrator();

  const pages: IFeatures[] = [
    home,
    prontoAtendimento,
    term,
    medicalcare,
    accesshistory,
    consents,
    managePractitioners,
    professinalRegister,
    profile,
    profileClinic,
    scheduler,
    register,
    scheduleManager,
    schedulingSearch,
    privacy,
    ehrrecords,
    examSolicitation,
    medicalPrescription,
    medicalNote,
    myfamily,
    wearableObservations,
    myRecords,
    patientSupportingDocuments,
    manageprotocol,
    managequestionnaire,
    createprotocol,
    createquestionnarie,
    personPdf,
    questionnaireRespondPage,
    fluxExtensions,
    {
      ...fluxzapCampaigns,
      // Mantém o Fluxzap imediatamente após "Extensões" no menu lateral.
      // Observação: o menu legado é ordenado por `sideMenuIndex` (ver `useLegacyNavigationModel`),
      // então garantimos o mesmo índice de Extensões para preservar a proximidade sem criar novo índice.
      sideMenuIndex: fluxExtensions.sideMenuIndex,
    },
    statsPanel,
    patientInsurance,
    managePlans,
    insuranceGuide,
    clinicalAnalysis,
    scheduleIntegrator,
  ];

  /**
   * Seções/grupos do menu lateral (legado).
   * Mantemos aqui porque o objetivo é centralizar definição de menu no registry.
   *
   * Importante:
   * - Esses itens não são "páginas finais" e podem renderizar vazio.
   * - Eles existem para agrupar subitens via `parent` nos hooks das páginas.
   */
  const sideBarSections: IFeatures[] = [
    {
      initial: false,
      active: access.type === "professional" && availableRoles?.length,
      avatarMenu: false,
      path: "/protocolsettings",
      icon: NotesRounded,
      text: "Protocolos clínicos",
      sideMenu: true,
      sideMenuIndex: 2,
      component: () => <ProtocolSettings />,
    },
    {
      initial: false,
      active:
        access.type === "professional" &&
        (currentFeatures.defaultApp ||
          availableRoles?.includes("manager") ||
          availableRoles?.includes("owner")),
      avatarMenu: false,
      path: "/insightssettings",
      icon: Insights,
      text: "Insights",
      sideMenu: true,
      sideMenuIndex: 3,
      component: () => <InsightsSettings />,
    },
    {
      initial: false,
      active:
        access.type === "professional" &&
        currentFeatures.insurance &&
        contractHealthPlan &&
        (availableRoles?.includes("manager") ||
          availableRoles?.includes("owner")),
      avatarMenu: false,
      path: "/insurancesettings",
      icon: HealthAndSafety,
      text: "Planos de saúde",
      sideMenu: true,
      sideMenuIndex: 0,
      component: () => <InsuranceSettings />,
    },
    {
      initial: false,
      active: access.type === "professional" && availableRoles?.length,
      avatarMenu: false,
      path: "/scheduleSettings",
      icon: Today,
      text: "Agendas",
      sideMenu: true,
      sideMenuIndex: 1,
      component: () => <ScheduleSettingsHub />,
    },
    {
      initial: false,
      active: access.type === "professional",
      avatarMenu: false,
      path: "/settings",
      icon: SettingsIcon,
      text: "Configurações",
      sideMenu: true,
      sideMenuIndex: 6,
      component: () => <SettingsHub />,
    },
  ];

  const allFeatures = useMemo(() => {
    return [...pages, ...sideBarSections];
  }, [pages, sideBarSections]);

  return useMemo(() => {
    const registryEntries: Array<[string, LegacyPageConfig]> = allFeatures.map(
      (feature) => {
        const pageName = getPageNameFromPath(feature.path);
        const meta = buildLegacyPageMetaFromFeature(feature);

        return [
          pageName,
          {
            path: feature.path,
            component: feature.component ?? (() => <></>),
            meta,
          },
        ];
      },
    );

    return Object.fromEntries(registryEntries);
  }, [allFeatures]);
}

/**
 * Helper para compatibilidade com o legado atual:
 * resolve a página corrente pelo parâmetro `id` e pelo `initial` da lista ativa.
 */
export function useLegacyCurrentPathFromLegacyRules(activeFeatures: IFeatures[]) {
  const { id } = useParams();

  return useMemo(() => {
    if (id) {
      return `/${id}`;
    }

    const initialPage = activeFeatures.find((page) => page.initial);
    return initialPage?.path;
  }, [activeFeatures, id]);
}

